import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import LogoutButton from './LogoutButton';
import authService from '../services/auth.service';
import notificationService, { AppNotification } from '../services/notification.service';

interface HeaderProps {
  onToggleSidebar: () => void;
}

const Header: React.FC<HeaderProps> = ({ onToggleSidebar }) => {
  const [user, setUser] = useState(authService.getCurrentUser());
  const [isMasquerading, setIsMasquerading] = useState(authService.isMasquerading());
  const [unreadCount, setUnreadCount] = useState(0);
  const [recentNotifications, setRecentNotifications] = useState<AppNotification[]>([]);
  const [stoppingMasquerade, setStoppingMasquerade] = useState(false);

  useEffect(() => {
    const syncAuthState = () => {
      setUser(authService.getCurrentUser());
      setIsMasquerading(authService.isMasquerading());
    };

    syncAuthState();
    window.addEventListener('auth-change', syncAuthState);
    return () => window.removeEventListener('auth-change', syncAuthState);
  }, []);

  useEffect(() => {
    if (!user) return;

    const loadNotificationSummary = async () => {
      try {
        const [countResponse, listResponse] = await Promise.all([
          notificationService.getUnreadCount(),
          notificationService.getNotifications(1, 5),
        ]);

        if (countResponse.success) {
          setUnreadCount(countResponse.count);
        }

        if (listResponse.success) {
          setRecentNotifications(listResponse.data.data.slice(0, 5));
        }
      } catch {
        // silently ignore header notification load failures
      }
    };

    loadNotificationSummary();
    const interval = setInterval(loadNotificationSummary, 60000);
    return () => clearInterval(interval);
  }, [user]);

  const handleStopMasquerade = async () => {
    setStoppingMasquerade(true);
    try {
      await authService.stopMasquerade();
      toast.success('Returned to your account');
      window.location.href = '/admin/users';
    } catch (error: any) {
      toast.error(error.message || 'Failed to stop masquerade session');
    } finally {
      setStoppingMasquerade(false);
    }
  };

  return (
    <header className='app-header'>
      <div className='header-content'>
        <div className='header-left'>
          <button
            className='sidebar-toggle d-lg-none'
            onClick={onToggleSidebar}
          >
            <i className='ri-menu-line'></i>
          </button>
          <div className='header-search d-none d-lg-flex'>
            <input
              type='search'
              className='form-control'
              placeholder='Search...'
            />
            <button className='btn'>
              <i className='ri-search-line'></i>
            </button>
          </div>
        </div>

        <div className='header-right'>
          <div className='header-element notifications-dropdown'>
            <a
              href='#'
              className='dropdown-toggle leading-none header-link d-flex justify-content-center position-relative'
              data-bs-toggle='dropdown'
              data-bs-auto-close='outside'
              aria-expanded='false'
            >
              <i className='ri-notification-3-line fs-20'></i>
              {unreadCount > 0 && (
                <span
                  className='position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger'
                  style={{ fontSize: '10px' }}
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </a>
            <div className='main-header-dropdown dropdown-menu dropdown-menu-end overflow-hidden' style={{ minWidth: '320px' }}>
              <div className='p-3 border-bottom d-flex justify-content-between align-items-center'>
                <h6 className='mb-0'>Notifications</h6>
                {unreadCount > 0 && (
                  <span className='badge bg-primary'>{unreadCount} unread</span>
                )}
              </div>
              <div className='list-group list-group-flush'>
                {recentNotifications.length === 0 ? (
                  <div className='p-3 text-muted small'>No notifications</div>
                ) : (
                  recentNotifications.map((notification) => (
                    <Link
                      key={notification.id}
                      to={notification.data?.case_id ? `/admin/cases/${notification.data.case_id}` : '/dashboard/notifications'}
                      className={`list-group-item list-group-item-action ${notification.read_at ? '' : 'fw-semibold'}`}
                    >
                      <div className='small'>{notification.title}</div>
                      <div className='text-muted' style={{ fontSize: '12px' }}>
                        {new Date(notification.created_at).toLocaleString()}
                      </div>
                    </Link>
                  ))
                )}
              </div>
              <div className='p-2 border-top text-center'>
                <Link to='/dashboard/notifications' className='small'>
                  View all notifications
                </Link>
              </div>
            </div>
          </div>

          <div className='header-element profile-1'>
            <a
              href='#'
              className='dropdown-toggle leading-none header-link d-flex justify-content-center'
              data-bs-toggle='dropdown'
              data-bs-auto-close='outside'
              aria-expanded='false'
            >
              <div
                className='rounded-circle d-flex align-items-center justify-content-center bg-primary'
                style={{ width: 32, height: 32 }}
              >
                <span
                  className='text-white fw-semibold'
                  style={{ fontSize: '14px' }}
                >
                  {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
                </span>
              </div>
            </a>
            <div className='main-header-dropdown dropdown-menu dropdown-menu-end overflow-hidden profile-dropdown'>
              <div className='main-header-profile bg-primary border-bottom p-3'>
                <div className='d-flex wd-100p'>
                  <div className='main-img-user'>
                    <div
                      className='rounded-circle bg-white d-flex align-items-center justify-content-center'
                      style={{ width: 45, height: 45 }}
                    >
                      <span
                        className='text-primary fw-semibold'
                        style={{ fontSize: '18px' }}
                      >
                        {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
                      </span>
                    </div>
                  </div>
                  <div className='ms-2 my-auto text-white'>
                    <h6 className='mb-0 text-fixed-white'>
                      {user?.name || 'User'}
                    </h6>
                    <span className='text-fixed-white op-7 fs-13'>
                      {user?.email || 'user@example.com'}
                    </span>
                  </div>
                </div>
              </div>
              <Link className='dropdown-item d-flex' to='/dashboard/notifications'>
                <i className='ri-notification-3-line fs-18 me-2'></i>Notifications
              </Link>
              <Link className='dropdown-item d-flex' to='/dashboard/profile'>
                <i className='ri-user-line fs-18 me-2'></i>Profile
              </Link>
              {authService.hasRole('admin') && !isMasquerading && (
                <Link className='dropdown-item d-flex' to='/admin/settings'>
                  <i className='ri-settings-line fs-18 me-2'></i>Settings
                </Link>
              )}
              {isMasquerading && (
                <button
                  type='button'
                  className='dropdown-item d-flex border-0 bg-transparent w-100 text-start'
                  onClick={handleStopMasquerade}
                  disabled={stoppingMasquerade}
                >
                  <i className='ri-user-shared-line fs-18 me-2'></i>
                  {stoppingMasquerade ? 'Switching back…' : 'Stop masquerading'}
                </button>
              )}
              <div className='dropdown-divider'></div>
              <LogoutButton className='dropdown-item d-flex' />
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
